import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Avatar,
  IconButton,
  CircularProgress,
  Badge,
  InputAdornment,
  Paper
} from '@mui/material';
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  InsertEmoticon as EmojiIcon,
  MoreVert as MoreVertIcon,
  Person as PersonIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';

import { formatTimestamp } from '../utils/helpers';

const LiveChatTab = ({ 
  visible,
  chatHistory = [],
  message = '',
  setMessage,
  sending = false,
  handleSendMessage,
  chatContainerRef,
  theme,
  activeUser,
  onlineUsers = [],
  activeSection = 'inbox',
  activeTicket = null, // Added activeTicket prop
  onBackToTickets,     // Added back button handler
  isMobile = false      // Added isMobile prop
}) => {
  const [attachMenuAnchor, setAttachMenuAnchor] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState(null);
  const messagesEndRef = useRef(null);
  
  // Format the chat message for display
  const formatChatMessage = (content) => {
    if (!content) return '';
    
    // Replace line breaks with <br> tags
    let formattedText = content.replace(/\n/g, '<br>');
    
    // Format URLs
    formattedText = formattedText.replace(
      /(https?:\/\/[^\s]+)/g, 
      '<a href="$1" target="_blank" rel="noopener noreferrer" style="color: #4C85F0; text-decoration: underline;">$1</a>'
    );
    
    // Format bold text
    formattedText = formattedText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    // Format italic text
    formattedText = formattedText.replace(/\*(.*?)\*/g, '<em>$1</em>');
    
    // Code blocks
    formattedText = formattedText.replace(/`(.*?)`/g, '<code style="background-color: rgba(0,0,0,0.1); padding: 2px 4px; border-radius: 3px;">$1</code>');
    
    return formattedText;
  };

  // Handle message input change
  const handleMessageChange = (e) => {
    setMessage(e.target.value);
    
    // Show typing indicator
    setIsTyping(true);
    
    // Clear any existing timeout
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }
    
    // Set new timeout to clear typing indicator
    const timeout = setTimeout(() => {
      setIsTyping(false);
    }, 1000);
    
    setTypingTimeout(timeout);
  };

  // Handle key press (Enter to send)
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (message.trim()) {
        handleSendMessage();
      }
    }
  };

  // Automatically scroll to bottom when chat history changes or visibility changes
  useEffect(() => {
    if (visible && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [activeTicket?.messages, visible]);
  
  // Early return if not visible
  if (!visible) return null;

  return (
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      overflow: 'hidden'
    }}>
      {/* Chat header */}
      <Box 
        sx={{ 
          p: 2, 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          bgcolor: 'rgba(30, 40, 55, 0.5)'
        }}
      >
        {isMobile && activeTicket && (
          <IconButton
            edge="start"
            color="inherit"
            onClick={onBackToTickets}
            sx={{ mr: 2, color: 'white' }}
          >
            <ArrowBackIcon />
          </IconButton>
        )}
        
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          {activeTicket ? (
            <>
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                variant="dot"
                color={activeTicket.status === 'open' ? "success" : activeTicket.status === 'pending' ? "warning" : "default"}
              >
                <Avatar
                  src={activeTicket.customer?.avatar}
                  alt={activeTicket.customer?.name}
                  sx={{ mr: 1, width: 40, height: 40 }}
                >
                  {!activeTicket.customer?.avatar && activeTicket.customer?.name?.charAt(0)}
                </Avatar>
              </Badge>
              <Box sx={{ ml: 1 }}>
                <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 'medium' }}>
                  {activeTicket.subject || 'Untitled Ticket'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  {activeTicket.customer?.name} • ID: {activeTicket.id}
                </Typography>
              </Box>
            </>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar sx={{ mr: 1, bgcolor: theme?.palette.primary.main || 'primary.main' }}>
                <PersonIcon />
              </Avatar>
              <Typography variant="subtitle1" sx={{ color: 'white' }}>
                {activeTicket ? 'Ticket Chat' : 'No Ticket Selected'}
              </Typography>
            </Box>
          )}
        </Box>
        
        {activeTicket && (
          <Box>
            <IconButton 
              size="small"
              sx={{ ml: 1, color: 'rgba(255,255,255,0.7)' }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>
      
      {/* Main Chat Area */}
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'rgba(18, 18, 24, 0.4)'
        }}
      >
        {/* Chat messages */}
        <Box
          ref={chatContainerRef}
          sx={{
            p: 2,
            flexGrow: 1,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 1
          }}
        >
          {!activeTicket ? (
            // No selected ticket view
            <Box 
              sx={{ 
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                opacity: 0.6
              }}
            >
              <Typography variant="h6" sx={{ color: 'white', textAlign: 'center', mb: 2 }}>
                No Ticket Selected
              </Typography>
              <Typography variant="body1" sx={{ color: 'white', textAlign: 'center' }}>
                Select a ticket to view the conversation.
              </Typography>
            </Box>
          ) : activeTicket.messages && activeTicket.messages.length > 0 ? (
            // Show ticket messages
            activeTicket.messages.map((msg, index) => (
              <Box
                key={msg.id || index}
                sx={{
                  display: 'flex',
                  flexDirection: msg.senderId === 'user-self' || msg.senderId === 'current-user' ? 'row-reverse' : 'row',
                  mb: 1
                }}
              >
                <Avatar
                  src={msg.senderId === 'user-self' || msg.senderId === 'current-user' ? '' : activeTicket.customer?.avatar}
                  sx={{
                    width: 32,
                    height: 32,
                    mr: msg.senderId === 'user-self' || msg.senderId === 'current-user' ? 0 : 1,
                    ml: msg.senderId === 'user-self' || msg.senderId === 'current-user' ? 1 : 0,
                    bgcolor: msg.senderId === 'user-self' || msg.senderId === 'current-user' ? 'primary.main' : 'secondary.main'
                  }}
                >
                  {msg.senderId === 'user-self' || msg.senderId === 'current-user' ? 
                    'A' : 
                    activeTicket.customer?.name?.charAt(0)
                  }
                </Avatar>
                
                <Box
                  sx={{
                    maxWidth: '70%',
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: msg.senderId === 'user-self' || msg.senderId === 'current-user' 
                      ? 'rgba(76, 133, 240, 0.2)'
                      : 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    position: 'relative',
                    mb: 1,
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 10,
                      left: msg.senderId === 'user-self' || msg.senderId === 'current-user' ? 'auto' : '-6px',
                      right: msg.senderId === 'user-self' || msg.senderId === 'current-user' ? '-6px' : 'auto',
                      width: 10,
                      height: 10,
                      backgroundColor: msg.senderId === 'user-self' || msg.senderId === 'current-user' 
                        ? 'rgba(76, 133, 240, 0.2)'
                        : 'rgba(255, 255, 255, 0.1)',
                      transform: 'rotate(45deg)'
                    }
                  }}
                >
                  <Typography 
                    variant="body2" 
                    component="div"
                    sx={{ wordBreak: 'break-word' }}
                    dangerouslySetInnerHTML={{ __html: formatChatMessage(msg.content) }}
                  />
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      display: 'block', 
                      textAlign: msg.senderId === 'user-self' || msg.senderId === 'current-user' ? 'right' : 'left',
                      mt: 0.5,
                      color: 'rgba(255,255,255,0.5)',
                      fontSize: '0.7rem'
                    }}
                  >
                    {msg.senderName} • {formatTimestamp(msg.timestamp || new Date().toISOString())}
                  </Typography>
                </Box>
              </Box>
            ))
          ) : (
            // Empty ticket messages view
            <Box 
              sx={{ 
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                opacity: 0.6
              }}
            >
              <Typography variant="body1" sx={{ color: 'white', textAlign: 'center' }}>
                No messages in this ticket yet
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', textAlign: 'center', mt: 1 }}>
                Send a message to start the conversation
              </Typography>
            </Box>
          )}
          
          {/* Typing indicator */}
          {isTyping && (
            <Box 
              sx={{ 
                display: 'flex',
                alignItems: 'center',
                alignSelf: 'flex-end',
                mb: 1
              }}
            >
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', mr: 1 }}>
                Typing
              </Typography>
              <Box sx={{ display: 'flex' }}>
                <Box className="typing-dot"></Box>
                <Box className="typing-dot"></Box>
                <Box className="typing-dot"></Box>
              </Box>
            </Box>
          )}
          
          {/* Empty div for scrolling to bottom */}
          <div ref={messagesEndRef} />
        </Box>
        
        {/* Message input area - only visible when a ticket is selected */}
        {activeTicket && (
          <Box
            sx={{
              p: 2,
              borderTop: '1px solid rgba(255, 255, 255, 0.1)',
              bgcolor: 'rgba(30, 40, 55, 0.5)',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <IconButton 
              size="small" 
              sx={{ mr: 1, color: 'rgba(255,255,255,0.7)' }}
              onClick={(e) => setAttachMenuAnchor(e.currentTarget)}
            >
              <AttachFileIcon fontSize="small" />
            </IconButton>
            
            <IconButton 
              size="small" 
              sx={{ mr: 1, color: 'rgba(255,255,255,0.7)' }}
            >
              <EmojiIcon fontSize="small" />
            </IconButton>
            
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Type a message..."
              value={message}
              onChange={handleMessageChange}
              onKeyPress={handleKeyPress}
              disabled={sending || !activeTicket}
              multiline
              maxRows={4}
              size="small"
              InputProps={{
                sx: {
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                  color: 'white',
                  borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.1)'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme?.palette.primary.main || 'rgba(76, 133, 240, 1)'
                  }
                }
              }}
            />
            
            <Button
              variant="contained"
              color="primary"
              disabled={!message.trim() || sending || !activeTicket}
              onClick={handleSendMessage}
              sx={{ ml: 1, minWidth: 'unset', width: 40, height: 40, p: 0 }}
            >
              {sending ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                <SendIcon />
              )}
            </Button>
          </Box>
        )}
      </Box>
      
      <style jsx global>{`
        .typing-dot {
          display: inline-block;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: white;
          margin-right: 4px;
          animation: typing 1.4s infinite both;
        }
        .typing-dot:nth-child(2) {
          animation-delay: 0.2s;
        }
        .typing-dot:nth-child(3) {
          animation-delay: 0.4s;
        }
        @keyframes typing {
          0%, 100% {
            opacity: 0.3;
            transform: scale(0.8);
          }
          50% {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </Box>
  );
};

export default LiveChatTab;
